<template>
  <v-container>
    <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 8px; justify-content: center; margin-bottom: 16px;">
      <v-btn color="primary" :to="{ name: 'TournamentView' }">Return to Tournament</v-btn>
      <v-btn v-if="maxTables > 0" @click="viewMode = 'list'" :variant="viewMode === 'list' ? 'tonal' : 'outlined'">List View</v-btn>
      <v-btn v-if="maxTables > 0" @click="viewMode = 'table'" :variant="viewMode === 'table' ? 'tonal' : 'outlined'">Table View</v-btn>
      <v-btn v-if="viewMode === 'table' && maxTables > 0" color="secondary" @click="randomlySeatPlayers">Randomly Seat All</v-btn>
    </div>
    
    <h1>Tournament Players</h1>

    <div v-if="maxTables === 0" class="text-center my-4 pa-4" style="background-color: #f0f0f0; border-radius: 8px;">
      <p>Table management is not enabled for this tournament.</p>
      <p class="text-caption">To enable it, set "Max Tables" to a value greater than 0 in the tournament settings.</p>
    </div>
    
    <div style="margin-bottom: 16px;">
      <div style="font-size: 1.1rem; margin: 8px 0;">
        Total Players: <strong>{{ players.length }}</strong>
      </div>
      <div style="font-size: 1.1rem; margin: 8px 0;">
        Players Busted: <strong>{{ bustedPlayers.length }}</strong>
      </div>
      <div style="font-size: 1.1rem; margin: 8px 0;">
        Players Left: <strong>{{ playersLeft }}</strong>
      </div>
    </div>
    
    <div v-if="viewMode === 'list' || maxTables === 0">
      <div style="overflow-x: auto; -webkit-overflow-scrolling: touch;">
      <v-table style="max-width: 900px; margin: auto;">
        <thead>
          <tr>
            <th>Player Name</th>
            <th>Entry Type</th>
            <th>Rebuys</th>
            <th>Add-on</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(entry, index) in allPlayerEntries" :key="entry.id">
            <td>
              <div class="d-flex align-center" style="gap: 8px;">
                <v-combobox
                  v-if="entry.type === 'original'"
                  :model-value="entry.name"
                  :items="allowedPlayerAliases"
                  :disabled="isPlayerBusted(entry.id)"
                  placeholder="Pick or type player name"
                  hide-details
                  density="compact"
                  style="max-width: 220px;"
                  @update:model-value="onPlayerNameChange(entry, $event)"
                />
                <span v-else>{{ entry.name }}</span>
                <v-tooltip v-if="entry.type === 'original' && entry.linkedUserAlias" location="top">
                  <template #activator="{ props }">
                    <v-icon v-bind="props" size="small" color="success">mdi-account-check</v-icon>
                  </template>
                  <span>Results will sync to this player's session list when you save tournament results.</span>
                </v-tooltip>
              </div>
            </td>
            <td>
              <span v-if="entry.type === 'original'">Original</span>
              <span v-else-if="entry.type === 'rebuy'">Rebuy {{ entry.rebuyNumber }}</span>
            </td>
            <td>
              <v-text-field
                v-model.number="entry.rebuys"
                type="number"
                min="0"
                :disabled="!rebuyAllowed || !isRebuyStillAllowed || entry.type !== 'original' || isPlayerBusted(entry.id)"
                hide-details
                style="max-width: 100px;"
                @input="updatePlayerRebuys(entry)"
              />
            </td>
            <td>
              <v-checkbox
                v-model="entry.addOn"
                :disabled="!addonAllowed || entry.type !== 'original'"
                hide-details
                @change="updatePlayerAddOn(entry)"
              />
            </td>
            <td>
              <span v-if="isPlayerBusted(entry.id) || !isCurrentLife(entry)" style="color: red; font-weight: bold;">
                Busted ({{ getBustOrder(entry.id) }})
              </span>
              <span v-else style="color: green; font-weight: bold;">
                Active
              </span>
            </td>
            <td>
              <div style="display: flex; gap: 8px;">
                <v-btn
                  v-if="isCurrentLife(entry) && !isPlayerBusted(entry.id)"
                  icon
                  color="error"
                  @click="bustPlayer(entry)"
                  size="small"
                >
                  <v-icon>mdi-skull</v-icon>
                </v-btn>
                <v-btn
                  v-if="isCurrentLife(entry) && entry.type === 'original'"
                  icon
                  color="error"
                  @click="requestDeletePlayer(entry.playerId)"
                  :disabled="players.length <= 2 || isPlayerBusted(entry.id)"
                  size="small"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
                <v-btn
                  v-if="rebuyAllowed && isRebuyStillAllowed && isCurrentLife(entry) && isPlayerBusted(entry.id)"
                  color="warning"
                  size="small"
                  @click="lateRebuy(entry)"
                >
                  <v-icon start>mdi-refresh</v-icon>
                  Rebuy
                </v-btn>
                <v-btn
                  v-if="isCurrentLife(entry) && isPlayerBusted(entry.id) && String(entry.id) === String(lastBustedPlayerId)"
                  color="secondary"
                  size="small"
                  @click="undoBust"
                >
                  <v-icon start>mdi-undo</v-icon>
                  Undo
                </v-btn>
                <template v-if="entry.type === 'original' && index === lastActiveEntryIndex">
                  <v-btn
                    color="primary"
                    @click="addPlayer"
                    :disabled="isAtMaxPlayers"
                    size="small"
                  >
                    <v-icon>mdi-plus</v-icon>
                    Add
                  </v-btn>
                  <v-text-field
                    v-model.number="addCount"
                    type="number"
                    min="1"
                    hide-details
                    density="compact"
                    style="max-width: 64px;"
                  />
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </v-table>
      </div>
    </div>

    <!-- Table View -->
    <div v-if="viewMode === 'table' && maxTables > 0">
      <div class="table-grid">
        <v-card v-for="tableNumber in activeTableNumbers" :key="`table-${tableNumber}`" class="table-card">
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Table {{ tableNumber }} — {{ playersByTable[tableNumber]?.length ?? 0 }} players</span>
            <v-btn v-if="canCloseTable(tableNumber)" color="warning" size="small" @click="openCloseTableDialog(tableNumber)">
              Close Table
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list dense>
              <v-list-item v-for="slot in seatSlotsByTable[tableNumber]" :key="slot.seat">
                <template v-slot:prepend>
                  <span class="text-caption text-medium-emphasis" style="min-width: 52px;">Seat {{ slot.seat }}</span>
                </template>
                <v-list-item-title v-if="slot.player">{{ slot.player.name }}</v-list-item-title>
                <v-list-item-title v-else class="text-medium-emphasis" style="font-style: italic;">— open —</v-list-item-title>
                <template v-slot:append>
                  <v-menu v-if="slot.player" offset-y>
                    <template v-slot:activator="{ props }">
                      <v-btn icon="mdi-dots-vertical" size="small" v-bind="props"></v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="openMovePlayerDialog(slot.player)">
                        <v-list-item-title>Move Player</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="bustPlayer(slot.player)">
                        <v-list-item-title>Bust Player</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="requestDeletePlayer(slot.player.id)">
                        <v-list-item-title>Delete Player</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </div>

      <div v-if="unseatedPlayers.length > 0" class="mt-8">
        <h3 class="mb-2">Unseated Players</h3>
        <v-card class="unseated-card">
          <v-card-text>
            <v-list dense>
              <v-list-item v-for="player in unseatedPlayers" :key="player.id">
                <v-list-item-title>{{ player.name }}</v-list-item-title>
                 <template v-slot:append>
                  <v-menu offset-y>
                    <template v-slot:activator="{ props }">
                      <v-btn icon="mdi-dots-vertical" size="small" v-bind="props"></v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="openMovePlayerDialog(player)"><v-list-item-title>Seat Player</v-list-item-title></v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <div v-if="feedback" style="margin-top: 16px; color: green;">{{ feedback }}</div>
    
    <!-- Bust Confirmation Dialog -->
    <v-dialog v-model="bustDialog" max-width="500">
      <v-card>
        <v-card-title>Player Busted</v-card-title>
        <v-card-text>
          <div style="margin-bottom: 16px;">
            <strong>{{ bustDialogData.playerName }}</strong> has been knocked out.
            <div v-if="String(bustDialogData.playerId).includes('_rebuy_')" style="color: #666; font-size: 0.9rem; margin-top: 4px;">
              This is a rebuy entry - player can continue with another rebuy if available.
            </div>
          </div>
          <v-select
            v-model="bustDialogData.knockedOutBy"
            :items="activePlayerNames"
            label="Knocked out by"
            placeholder="Select or type player name"
            clearable
            filterable
            style="margin-bottom: 16px;"
          />
          <v-checkbox
            v-model="bustDialogData.didRebuy"
            label="Player chose to rebuy"
            :disabled="!rebuyAllowed"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelBust">Cancel</v-btn>
          <v-btn color="primary" text @click="confirmBust">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Delete Player</v-card-title>
        <v-card-text>Are you sure you want to delete <strong>{{ playerToDeleteName }}</strong>?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelDeletePlayer">Cancel</v-btn>
          <v-btn color="error" text @click="confirmDeletePlayer">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Move Player Dialog -->
    <v-dialog v-model="movePlayerDialog" max-width="400">
      <v-card>
        <v-card-title>Move {{ playerToMove?.name }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="moveToTable"
            :items="Array.from({ length: maxTables }, (_, i) => i + 1)"
            label="Select Table"
          ></v-select>
          <v-select
            v-model="moveToSeat"
            :items="availableSeatsForMove"
            label="Select Seat (optional — blank for random)"
            clearable
            class="mt-2"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="movePlayerDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="confirmMovePlayer">Move</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unbalanced Move Confirmation Dialog -->
    <v-dialog v-model="unbalancedMoveDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="headline">Confirm Unbalanced Move</v-card-title>
        <v-card-text>
          This move will result in unbalanced tables. Are you sure you want to proceed?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="cancelUnbalancedMove">Cancel</v-btn>
          <v-btn color="warning" text @click="executeMove">Confirm Move</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Table Recommendation Dialog -->
    <v-dialog v-model="closeTableDialog" max-width="450">
      <v-card>
        <v-card-title class="headline">Table Consolidation</v-card-title>
        <v-card-text>
          A table can be closed. It is recommended to move players from the smallest table to consolidate.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeTableDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Close Table Confirmation Dialog -->
    <v-dialog v-model="closeTableConfirmDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">Close Table {{ tableToClose }}</v-card-title>
        <v-card-text>
          Do you want to randomly re-seat the players from this table at other tables?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="handleReseatChoice(false)">No, Unseat Them</v-btn>
          <v-btn color="primary" text @click="handleReseatChoice(true)">Yes, Re-Seat Them</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rebalance Warning Dialog -->
    <v-dialog v-model="rebalanceDialog" max-width="450">
      <v-card>
        <v-card-title class="headline">Rebalance Recommended</v-card-title>
        <v-card-text>
          A table now has significantly fewer players. It is recommended to rebalance the tables to ensure fair play.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="rebalanceDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { db } from '@/firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';
// eslint-disable-next-line no-unused-vars -- TOURNAMENT_PAYOUTS_DB used in autoSave callback
import { TOURNAMENT_PLAYERS_DB, TOURNAMENT_PAYOUTS_DB } from '@/constants/constants';
import { useTournamentStore } from '@/stores/tournament';
import { useUserStore } from '@/stores/users';
import { useRouter } from 'vue-router';
import { getAllowedPlayerOptions } from '@/api/users.js';
import { getLeagueTournaments } from '@/api/leagues.js';
import { setLinkedState, computeTablesNeeded, distributePlayersToTables, isTableUnbalancedAfterBust } from '@/utils/playerUtils.js';

const router = useRouter();
const userStore = useUserStore();
const tournamentStore = useTournamentStore();

// Allowed player options for dropdown: users who have granted this tournament runner permission to add them
const allowedPlayerOptions = ref([]);
const allowedPlayerAliases = computed(() => allowedPlayerOptions.value.map(o => o.alias));

const tournamentName = computed(() => {
  if (!tournamentStore.tournamentName) {
    return '';
  }
  return tournamentStore.tournamentName;
});

// Add watcher for tournamentName
watch(tournamentName, (newVal) => {
  if (!newVal) {
    console.error('No tournament name found in store');
    router.push({ name: 'TournamentView' });
  }
}, { immediate: true });

// Watch store for level updates
watch(() => tournamentStore.timerState?.level, (newLevel) => {
  if (newLevel !== undefined) {
    currentLevel.value = newLevel;
  }
});

watch(() => tournamentStore.timerState?.normalLevelCount, (newCount) => {
  if (newCount !== undefined) {
    normalLevelCount.value = newCount;
  }
});

watch(() => tournamentStore.levels, (newLevels) => {
  if (newLevels) {
    levels.value = newLevels;
  }
});

watch(() => tournamentStore.settings.rebuyEndLevel, (newValue) => {
  rebuyEndLevel.value = newValue ?? null;
});

const players = ref([]);
const bustedPlayers = ref([]);
const addCount = ref(1);
const rebuyAllowed = ref(false);
const addonAllowed = ref(false);
const feedback = ref('');
const autoSaveTimeout = ref(null);
const bustDialog = ref(false);
const bustDialogData = ref({
  playerId: null,
  playerName: '',
  knockedOutBy: '',
  didRebuy: false
});
const deleteDialog = ref(false);
const playerToDelete = ref(null);
const viewMode = ref((tournamentStore.settings.maxTables ?? 0) > 0 ? 'table' : 'list');

// Table management state
const movePlayerDialog = ref(false);
const playerToMove = ref(null);
const moveToTable = ref(null);
const moveToSeat = ref(null);
const rebalanceDialog = ref(false);
const unbalancedMoveDialog = ref(false);
const closeTableDialog = ref(false);
const closeTableConfirmDialog = ref(false);
const tableToClose = ref(null);
let pendingMove = null;

// Define `buyinAmount` and `addonAmount` as reactive variables
const buyinAmount = ref(0);
const rebuyAmount = ref(0);
const addonAmount = ref(0);
const payoutTables = ref([]);
const maxTables = ref(0);
const maxPlayersPerTable = ref(10);

// Initialize data from store
players.value = tournamentStore.players
bustedPlayers.value = tournamentStore.bustedPlayers
rebuyAllowed.value = tournamentStore.settings.rebuyAllowed
const rebuyEndLevel = ref(tournamentStore.settings.rebuyEndLevel ?? null);
addonAllowed.value = tournamentStore.settings.addonAllowed
buyinAmount.value = tournamentStore.settings.buyinAmount
rebuyAmount.value = tournamentStore.settings.rebuyAmount || 0;
addonAmount.value = tournamentStore.settings.addonAmount
maxTables.value = tournamentStore.settings.maxTables ?? 0;
maxPlayersPerTable.value = tournamentStore.settings.maxPlayersPerTable ?? 10;
payoutTables.value = tournamentStore.settings.payoutTables || [];
const levels = ref(tournamentStore.levels || []);
const currentLevel = ref(tournamentStore.timerState?.level ?? 1);
const normalLevelCount = ref(tournamentStore.timerState?.normalLevelCount ?? 1);

// Computed properties
const playersLeft = computed(() => {
  let totalEntries = 0;
  players.value.forEach(player => {
    totalEntries += 1 + (player.rebuys || 0); // Original entry + rebuys
  });
  return totalEntries - bustedPlayers.value.length;
});

// Check if rebuys are still allowed based on current level
const isRebuyStillAllowed = computed(() => {
  // If rebuyEndLevel is not set or is 0, rebuys are always allowed (0 means all levels, backward compatibility)
  if (!rebuyEndLevel.value || rebuyEndLevel.value === 0) {
    return true;
  }
  
  // Get current normal level count (not including breaks)
  const currentNormalLevel = normalLevelCount.value;
  
  // If we're at or before the end level, rebuys are allowed
  if (currentNormalLevel <= rebuyEndLevel.value) {
    return true;
  }
  
  // Check if we're in a break right after the rebuy end level
  // Find the level index for the rebuy end level in the levels array
  let rebuyEndLevelIndex = -1;
  let normalLevelCounter = 0;
  for (let i = 0; i < levels.value.length; i++) {
    if (!levels.value[i].isBreak) {
      normalLevelCounter++;
      if (normalLevelCounter === rebuyEndLevel.value) {
        rebuyEndLevelIndex = i;
        break;
      }
    }
  }
  
  // If we found the rebuy end level, check if the next level is a break and we're currently in it
  if (rebuyEndLevelIndex >= 0 && rebuyEndLevelIndex + 1 < levels.value.length) {
    const nextLevel = levels.value[rebuyEndLevelIndex + 1];
    if (nextLevel.isBreak) {
      // currentLevel is 1-indexed (level 1, 2, 3...), so check if we're at the break level
      // rebuyEndLevelIndex is 0-indexed, so the break would be at index rebuyEndLevelIndex + 1
      // which corresponds to level number rebuyEndLevelIndex + 2 (since level 1 is index 0)
      const breakLevelNumber = rebuyEndLevelIndex + 2;
      if (currentLevel.value === breakLevelNumber) {
        return true;
      }
    }
  }
  
  return false;
});

const totalRebuys = computed(() => {
  return players.value.reduce((total, player) => total + (player.rebuys || 0), 0);
});

const totalAddOns = computed(() => {
  return players.value.reduce((total, player) => total + (player.addOn ? 1 : 0), 0);
});

// Add the `totalPot` computed property
const totalPot = computed(() => {
  let pot = players.value.length * buyinAmount.value;
  if (rebuyAllowed.value) {
    pot += totalRebuys.value * rebuyAmount.value; // Rebuys add to the pot
  }
  if (addonAllowed.value) {
    pot += totalAddOns.value * addonAmount.value; // Add-ons add to the pot
  }
  return pot;
});

// Get all player entries (including rebuys) for bust tracking
const allPlayerEntries = computed(() => {
  const entries = [];
  players.value.forEach(player => {
    // Add original entry (include linked state for session sync indicator)
    entries.push({
      id: player.id,
      name: player.name,
      type: 'original',
      playerId: player.id,
      rebuys: player.rebuys || 0,
      addOn: player.addOn || false,
      linkedUserAlias: player.linkedUserAlias,
      linkedUserId: player.linkedUserId
    });
    
    // Add rebuy entries
    for (let i = 0; i < (player.rebuys || 0); i++) {
      entries.push({
        id: `${player.id}_rebuy_${i + 1}`,
        name: player.name,
        type: 'rebuy',
        playerId: player.id,
        rebuyNumber: i + 1,
        addOn: player.addOn || false
      });
    }
  });
  
  // Sort entries: current-life active entries first, then everything else (busted/superseded originals)
  return entries.sort((a, b) => {
    const aActive = isCurrentLife(a) && !isPlayerBusted(a.id);
    const bActive = isCurrentLife(b) && !isPlayerBusted(b.id);

    if (aActive === bActive) {
      if (!aActive) {
        // Both in the busted/bottom section: sort by bust order descending
        const aBustOrder = getBustOrder(a.id) || 0;
        const bBustOrder = getBustOrder(b.id) || 0;
        return bBustOrder - aBustOrder;
      }
      return 0;
    }

    return aActive ? -1 : 1;
  });
});

// The playerId of the most recently busted player who has not rebuyed — Undo button target
const lastBustedPlayerId = computed(() => {
  for (let i = bustedPlayers.value.length - 1; i >= 0; i--) {
    if (!bustedPlayers.value[i].didRebuy) return bustedPlayers.value[i].playerId;
  }
  return null;
});

// Index of the last active (non-busted, current-life) original entry — Add button goes here
const lastActiveEntryIndex = computed(() => {
  const entries = allPlayerEntries.value;
  let last = -1;
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    if (e.type === 'original' && isCurrentLife(e) && !isPlayerBusted(e.id)) {
      last = i;
    }
  }
  // Fall back to last entry if everyone is busted
  if (last === -1) {
    for (let i = entries.length - 1; i >= 0; i--) {
      if (entries[i].type === 'original') return i;
    }
  }
  return last;
});

const activePlayerNames = computed(() => {
  const bustingEntryId = bustDialogData.value.playerId;
  const originalId = String(bustingEntryId).includes('_rebuy_')
    ? String(bustingEntryId).split('_rebuy_')[0]
    : bustingEntryId;
  const bustingPlayer = players.value.find(p => String(p.id) === String(originalId));
  const bustingTable = bustingPlayer?.table;

  return allPlayerEntries.value
    .filter(entry => isCurrentLife(entry))
    .filter(entry => !isPlayerBusted(entry.id))
    .filter(entry => String(entry.id) !== String(bustingEntryId))
    .filter(entry => {
      if (maxTables.value > 0 && bustingTable) {
        const player = players.value.find(p => p.id === entry.playerId);
        return player?.table === bustingTable;
      }
      return true;
    })
    .map(entry => entry.name)
    .filter(name => name && name.trim() !== '');
});

// Add a computed property to get the name of the player to delete
const playerToDeleteName = computed(() => {
  const player = players.value.find(p => p.id === playerToDelete.value);
  return player ? player.name : '';
});

const playersByTable = computed(() => {
  const tableMap = {};
  for (let i = 1; i <= maxTables.value; i++) {
    tableMap[i] = [];
  }
  players.value.forEach(player => {
    if (player.table && tableMap[player.table] && !isPlayerBusted(player.id)) {
      tableMap[player.table].push(player);
    }
  });
  // Sort players by seat number within each table
  for (const table in tableMap) {
    tableMap[table].sort((a, b) => (a.seat || 0) - (b.seat || 0));
  }
  return tableMap;
});

const unseatedPlayers = computed(() => {
  return players.value.filter(player => {
    const isActive = !isPlayerBusted(player.id);
    return isActive && (!player.table || player.table === 0);
  });
});

const activeTableNumbers = computed(() => {
  const activeTables = new Set();
  players.value.forEach(player => {
    // Only consider active (not busted) players for table display
    if (player.table && !isPlayerBusted(player.id)) {
      activeTables.add(player.table);
    }
  });
  return Array.from(activeTables).sort((a, b) => a - b);
});

// For each active table, produce an array of maxPlayersPerTable seat slots.
// Each slot is { seat: number, player: object|null }.
const seatSlotsByTable = computed(() => {
  const result = {};
  for (const tableNum of activeTableNumbers.value) {
    const slots = [];
    for (let seat = 1; seat <= maxPlayersPerTable.value; seat++) {
      const player = players.value.find(
        p => p.table === tableNum && p.seat === seat && !isPlayerBusted(p.id)
      );
      slots.push({ seat, player: player || null });
    }
    result[tableNum] = slots;
  }
  return result;
});

function canCloseTable(tableNumber) {
  if (maxTables.value <= 1 || activeTableNumbers.value.length <= 1) return false;

  const totalActivePlayers = players.value.filter(p => !isPlayerBusted(p.id)).length;
  const tablesInUse = activeTableNumbers.value.length;
  
  // Check if consolidation is possible (enough players to fit in one less table)
  const canConsolidate = totalActivePlayers <= (tablesInUse - 1) * maxPlayersPerTable.value;
  
  if (!canConsolidate) return false;
  
  // If a specific table number is provided, check if that table has players
  if (tableNumber !== undefined) {
    const playersAtTable = players.value.filter(
      p => p.table === tableNumber && !isPlayerBusted(p.id)
    );
    return playersAtTable.length > 0;
  }
  
  return true;
}

// Auto-save function
async function autoSave() {
  if (autoSaveTimeout.value) {
    clearTimeout(autoSaveTimeout.value);
  }

  autoSaveTimeout.value = setTimeout(async () => {
    if (!tournamentName.value || !db) return;

    // Only use tournamentName from store for document IDs
    const playersDoc = doc(db, TOURNAMENT_PLAYERS_DB, tournamentName.value);
    const payoutsDoc = doc(db, TOURNAMENT_PAYOUTS_DB, tournamentName.value);
    //('Auto-saving players and payouts for tournament:', tournamentName.value);

    try {
    // Update store state
    tournamentStore.players = players.value
    tournamentStore.bustedPlayers = bustedPlayers.value
    
    // Then save to Firestore
      // Save players data
      await setDoc(playersDoc, {
        players: players.value,
        bustedPlayers: bustedPlayers.value,
        totalRebuys: totalRebuys.value,
        totalAddOns: totalAddOns.value
      });

      // Save payouts data (if applicable)
      const numPlayers = players.value.length;
      const totalPotValue = totalPot.value;
      let calculatedPayouts = [];
      let numberOfPayouts = 0;

      if (payoutTables.value && payoutTables.value.length > 0 && totalPotValue > 0 && numPlayers > 0) {
        const payoutTable = payoutTables.value.find(
          (table) => numPlayers >= table.minEntries && numPlayers <= table.maxEntries
        );

        if (payoutTable) {
          numberOfPayouts = Math.min(payoutTable.numberOfPayouts, numPlayers);
          const template = payoutTable.prizesTemplate;
          const usePercent = payoutTable.usePercent !== undefined ? payoutTable.usePercent : true; // Default to true for backward compatibility
          
          if (numberOfPayouts > 0) {
            if (usePercent) {
              // Use percentage-based payouts
              let totalPaid = 0;
              calculatedPayouts = template.slice(0, numberOfPayouts - 1).map((percent, index) => {
                const amount = Math.round((totalPotValue * percent) / 100);
                totalPaid += amount;
                return { position: index + 1, amount };
              });

              const lastPlaceAmount = Math.max(0, totalPotValue - totalPaid);
              calculatedPayouts.push({ position: numberOfPayouts, amount: lastPlaceAmount });
            } else {
              // Use dollar amount-based payouts
              calculatedPayouts = template.slice(0, numberOfPayouts).map((amount, index) => {
                return { position: index + 1, amount: Math.round(amount) };
              });
            }
          }
        }
      }

      await setDoc(payoutsDoc, {
        numberOfPayouts,
        payouts: calculatedPayouts
      });

      feedback.value = 'Auto-saved!';
      setTimeout(() => {
        feedback.value = '';
      }, 2000);
    } catch (e) {
      console.error('Error auto-saving:', e);
      feedback.value = 'Error auto-saving.';
    }
  }, 1000);
}

// Returns true only for the entry that represents the player's current active life.
// For a player with rebuys, only their latest rebuy entry is their current life.
function isCurrentLife(entry) {
  const player = players.value.find(p => String(p.id) === String(entry.playerId ?? entry.id));
  const rebuys = player?.rebuys || 0;
  if (rebuys === 0) return entry.type === 'original';
  return entry.type === 'rebuy' && entry.rebuyNumber === rebuys;
}

function isPlayerBusted(playerId) {
  // Find the base player to determine their current life's entry id.
  // If the player has rebuys, their current life is `${id}_rebuy_${rebuys}`.
  // Only that entry appearing in bustedPlayers means they are currently out.
  const player = players.value.find(p => String(p.id) === String(playerId));
  const rebuys = player?.rebuys || 0;
  const currentEntryId = rebuys > 0 ? `${playerId}_rebuy_${rebuys}` : String(playerId);
  return bustedPlayers.value.some(bust => String(bust.playerId) === currentEntryId);
}

function getBustOrder(playerId) {
  // Try exact match first (covers rebuy entry ids like `abc_rebuy_1`)
  let bust = bustedPlayers.value.find(bust => String(bust.playerId) === String(playerId));
  if (bust) return bust.order;
  // Fall back to current-life entry id (covers original player ids when they have rebuys)
  const player = players.value.find(p => String(p.id) === String(playerId));
  if (player && player.rebuys > 0) {
    const currentEntryId = `${playerId}_rebuy_${player.rebuys}`;
    bust = bustedPlayers.value.find(bust => String(bust.playerId) === currentEntryId);
  }
  return bust ? bust.order : null;
}

function bustPlayer(entry) {
  // When called from the table view, entry is the raw player object (no `type` field).
  // Resolve the correct current-life entry id so bustedPlayers stores the right id.
  let entryId = entry.id;
  if (!entry.type) {
    const player = players.value.find(p => String(p.id) === String(entry.id));
    const rebuys = player?.rebuys || 0;
    entryId = rebuys > 0 ? `${entry.id}_rebuy_${rebuys}` : String(entry.id);
  }
  bustDialogData.value = {
    playerId: entryId,
    playerName: entry.name,
    knockedOutBy: '',
    didRebuy: false
  };
  bustDialog.value = true;
}

function confirmBust() {
  const { playerId, knockedOutBy, didRebuy } = bustDialogData.value;

  // Capture the busted player's table before they are added to bustedPlayers
  // (playersByTable excludes busted players, so we need this now)
  const originalId = String(playerId).includes('_rebuy_')
    ? String(playerId).split('_rebuy_')[0]
    : playerId;
  const bustedPlayer = players.value.find(p => String(p.id) === String(originalId));
  const bustedTable = bustedPlayer?.table ?? null;
  
  // Add to busted players list
  const bustOrder = bustedPlayers.value.length + 1;
  const tournamentSecondsAtBust = tournamentStore.timerState?.tournamentSecondsElapsed ?? 0;
  bustedPlayers.value.push({
    playerId,
    knockedOutBy,
    didRebuy,
    order: bustOrder,
    timestamp: Date.now(),
    tournamentSecondsAtBust
  });

  // If player rebought, increment their rebuy count and set re-entry tournament time (for duration tracking)
  if (didRebuy && rebuyAllowed.value) {
    const originalPlayerId = String(playerId).includes('_rebuy_') ? 
      String(playerId).split('_rebuy_')[0] : playerId;
    const player = players.value.find(p => p.id == originalPlayerId);
    if (player) {
      player.rebuys = (player.rebuys || 0) + 1;
      player.enteredTournamentSeconds = tournamentSecondsAtBust; // re-entry tournament time (this life)
    }
  }
  
  bustDialog.value = false;
  feedback.value = `${bustDialogData.value.playerName} has been busted!`;
  autoSave();
  if (!bustDialogData.value.didRebuy) checkTableConsolidation();
  checkTableBalance(bustedTable);
}

function cancelBust() {
  bustDialog.value = false;
}

// Allow a busted player to rebuy after the fact (runner forgot, or player changed mind)
function lateRebuy(entry) {
  const playerId = entry.playerId ?? entry.id;
  const originalId = String(playerId).includes('_rebuy_')
    ? String(playerId).split('_rebuy_')[0]
    : playerId;
  const player = players.value.find(p => String(p.id) === String(originalId));
  if (!player) return;

  // Mark the bust entry as didRebuy so it no longer shows the Rebuy button
  const bustEntry = bustedPlayers.value.find(b => String(b.playerId) === String(playerId));
  if (bustEntry) bustEntry.didRebuy = true;

  // Increment rebuy count — this creates the new rebuy life in allPlayerEntries
  player.rebuys = (player.rebuys || 0) + 1;
  player.enteredTournamentSeconds = tournamentStore.timerState?.tournamentSecondsElapsed ?? 0;

  feedback.value = `${player.name} has rebuyed!`;
  autoSave();
}

// Undo the last bust — only available on the most recently busted player
function undoBust() {
  const targetId = lastBustedPlayerId.value;
  if (!targetId) return;

  const bustIndex = bustedPlayers.value.findLastIndex(b => String(b.playerId) === String(targetId));
  if (bustIndex === -1) return;

  const bustEntry = bustedPlayers.value[bustIndex];

  // If the original bust had didRebuy=true (bust+rebuy in one step), undo the rebuy too
  if (bustEntry.didRebuy) {
    const originalId = String(targetId).includes('_rebuy_')
      ? String(targetId).split('_rebuy_')[0]
      : targetId;
    const player = players.value.find(p => String(p.id) === String(originalId));
    if (player && player.rebuys > 0) player.rebuys -= 1;
  }

  bustedPlayers.value.splice(bustIndex, 1);
  // Re-order remaining bust entries
  bustedPlayers.value.forEach((b, i) => { b.order = i + 1; });

  feedback.value = 'Last bust undone.';
  autoSave();
}

function requestDeletePlayer(playerId) {
  playerToDelete.value = playerId;
  deleteDialog.value = true;
}

function cancelDeletePlayer() {
  deleteDialog.value = false;
  playerToDelete.value = null;
}

function confirmDeletePlayer() {
  if (!playerToDelete.value) return;

  const playerIdToDelete = playerToDelete.value;

  // Remove from players array
  const playerIndex = players.value.findIndex(p => p.id === playerIdToDelete);
  if (playerIndex > -1) {
    players.value.splice(playerIndex, 1);
  }

  // Remove any bust entries for this player (including rebuys)
  bustedPlayers.value = bustedPlayers.value.filter(
    bust => !String(bust.playerId).startsWith(String(playerIdToDelete))
  );

  // Re-order remaining bust entries
  bustedPlayers.value.forEach((bust, index) => {
    bust.order = index + 1;
  });

  // Close dialog
  cancelDeletePlayer();
  
  // Save changes
  autoSave();
  checkTableConsolidation();
  checkTableBalance();
}


/** Sync linked state from player name using the preloaded allowed options. If name is in the list, link; else clear. */
function setLinkedStateFromName(player) {
  setLinkedState(player, allowedPlayerOptions.value);
}

/** Called when the player name combobox value changes (pick or type). */
function onPlayerNameChange(entry, newValue) {
  if (entry.type !== 'original') return;
  const player = players.value.find(p => p.id === entry.playerId);
  if (!player) return;
  const name = typeof newValue === 'string' ? newValue : (newValue?.alias ?? newValue ?? '');
  // Sync to store immediately so navigating away and back doesn't lose the change
  const trimmedName = (name || '').trim();
  // Don't allow the same player (name) to be added twice
  if (trimmedName) {
    const alreadyUsed = players.value.some(
      p => p.id !== player.id && (p.name || '').trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (alreadyUsed) {
      feedback.value = 'That player is already in the tournament.';
      setTimeout(() => { feedback.value = ''; }, 3000);
      return;
    }
  }
  player.name = trimmedName;
  setLinkedStateFromName(player);
  tournamentStore.players = players.value;
  autoSave();
}

// Helper functions to update player data when entries are modified
function updatePlayerRebuys(entry) {
  if (entry.type === 'original') {
    const player = players.value.find(p => p.id === entry.playerId);
    if (player) {
      player.rebuys = entry.rebuys;
      autoSave();
    }
  }
}

function updatePlayerAddOn(entry) {
  if (entry.type === 'original') {
    const player = players.value.find(p => p.id === entry.playerId);
    if (player) {
      player.addOn = entry.addOn;
      autoSave();
    }
  }
}

// Use the `initializeDefaultPlayers` function in the appropriate place
function initializeDefaultPlayers() {
  const now = Date.now();
  const enteredTournamentSeconds = tournamentStore.timerState?.tournamentSecondsElapsed ?? 0;
  players.value = [
    { id: now + Math.random(), name: 'Player 1', rebuys: 0, addOn: false, enteredAt: now, enteredTournamentSeconds },
    { id: now + Math.random() + 1, name: 'Player 2', rebuys: 0, addOn: false, enteredAt: now, enteredTournamentSeconds }
  ];
  bustedPlayers.value = [];
}

// Load allowed player options (users who can be picked by this tournament runner) and then tournament data
async function loadAllowedPlayerOptions() {
  const uid = userStore.userid;
  const alias = userStore.username;
  if (!uid) return;
  const options = await getAllowedPlayerOptions(uid);
  // Tournament runners can always link themselves without needing to explicitly grant themselves permission
  if (alias && userStore.hasPokerPermission && !options.some(o => o.alias === alias)) {
    options.unshift({ alias, user_id: uid });
  }
  allowedPlayerOptions.value = options;
}

// Load player names from previous tournaments in the same league, adding any not already in allowedPlayerOptions
async function loadLeaguePreviousPlayers(leagueId) {
  if (!leagueId) return;
  const tournaments = await getLeagueTournaments(leagueId);
  const existingAliases = new Set(allowedPlayerOptions.value.map(o => o.alias));
  const previousNames = new Set();
  for (const t of tournaments) {
    for (const p of (t.players || [])) {
      const name = (p.name || '').trim();
      if (name && !existingAliases.has(name)) {
        previousNames.add(name);
      }
    }
  }
  for (const name of previousNames) {
    allowedPlayerOptions.value.push({ alias: name });
  }
}

// Load players from Firestore on mount so after refresh we show saved data, not defaults
onMounted(async () => {
  if (!tournamentName.value) return;
  // Step 1: Find all aliases allowed to be picked by this tournament runner
  await loadAllowedPlayerOptions();
  // Only reload from Firestore if the store has no player data (fresh page load / hard refresh).
  // If the store already has players for this tournament, use that — it reflects any unsaved
  // in-memory edits (e.g. names typed but not yet saved) that would be lost by a Firestore reload.
  const storeHasPlayers = Array.isArray(tournamentStore.players) && tournamentStore.players.length > 0;
  if (!storeHasPlayers) {
    await tournamentStore.loadTournamentData();
  }
  // Add unregistered names from previous tournaments in the same league to the dropdown
  await loadLeaguePreviousPlayers(tournamentStore.settings.leagueId);
  // Sync refs from store after load
  players.value = tournamentStore.players || [];
  bustedPlayers.value = tournamentStore.bustedPlayers || [];
  rebuyAllowed.value = tournamentStore.settings.rebuyAllowed;
  addonAllowed.value = tournamentStore.settings.addonAllowed;
  buyinAmount.value = tournamentStore.settings.buyinAmount;
  rebuyAmount.value = tournamentStore.settings.rebuyAmount || 0;
  addonAmount.value = tournamentStore.settings.addonAmount;
  maxTables.value = tournamentStore.settings.maxTables ?? 0;
  maxPlayersPerTable.value = tournamentStore.settings.maxPlayersPerTable ?? 10;
  payoutTables.value = tournamentStore.settings.payoutTables || [];
  levels.value = tournamentStore.levels || [];
  currentLevel.value = tournamentStore.timerState?.level ?? 1;
  normalLevelCount.value = tournamentStore.timerState?.normalLevelCount ?? 1;
  if (tournamentStore.settings.rebuyEndLevel !== undefined && tournamentStore.settings.rebuyEndLevel !== null) {
    rebuyEndLevel.value = tournamentStore.settings.rebuyEndLevel;
  }
  viewMode.value = maxTables.value > 0 ? 'table' : 'list';
  // Only initialize default two players if we still have none (new tournament)
  if (players.value.length === 0) {
    initializeDefaultPlayers();
    autoSave();
  } else {
    // Step 3/4: If player name is in allowed list, link them; else no link
    players.value.forEach(p => setLinkedStateFromName(p));
  }
});

function addPlayer() {
  const count = Math.max(1, Math.floor(addCount.value) || 1);
  const enteredTournamentSeconds = tournamentStore.timerState?.tournamentSecondsElapsed ?? 0;
  for (let i = 0; i < count; i++) {
    const now = Date.now() + i; // slight offset to guarantee unique IDs
    const newPlayer = {
      id: now + Math.random(),
      name: `Player ${players.value.length + 1}`,
      rebuys: 0,
      addOn: false,
      enteredAt: now,
      enteredTournamentSeconds
    };
    players.value.push(newPlayer);
    setLinkedStateFromName(newPlayer);
  }
  autoSave();
}

function randomlySeatPlayers() {
  const activePlayers = players.value.filter(p => !isPlayerBusted(p.id));
  if (activePlayers.length === 0) return;

  const tablesToUse = computeTablesNeeded(activePlayers.length, maxPlayersPerTable.value, maxTables.value);
  if (tablesToUse === 0) return;

  // Shuffle players (Fisher-Yates)
  for (let i = activePlayers.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [activePlayers[i], activePlayers[j]] = [activePlayers[j], activePlayers[i]];
  }

  // Unseat everyone first
  players.value.forEach(p => {
    p.table = null;
    p.seat = null;
  });

  distributePlayersToTables(activePlayers, tablesToUse, maxPlayersPerTable.value);
  autoSave();
}

const availableSeatsForMove = computed(() => {
  if (!moveToTable.value) return [];
  const occupied = new Set(
    (playersByTable.value[moveToTable.value] || [])
      .filter(p => !playerToMove.value || p.id !== playerToMove.value.id)
      .map(p => p.seat)
  );
  // Also exclude the player's own current seat so they can't "move" to where they already are
  if (playerToMove.value?.table === moveToTable.value && playerToMove.value?.seat) {
    occupied.add(playerToMove.value.seat);
  }
  const seats = [];
  for (let s = 1; s <= maxPlayersPerTable.value; s++) {
    if (!occupied.has(s)) seats.push(s);
  }
  return seats;
});

function openMovePlayerDialog(player) {
  playerToMove.value = player;
  moveToSeat.value = null;

  // Default to the least-populated table when tables are unbalanced and this
  // player is at the most-populated table.
  const counts = activeTableNumbers.value.map(t => ({
    table: t,
    count: (playersByTable.value[t] || []).length
  }));
  if (counts.length > 1) {
    const maxCount = Math.max(...counts.map(c => c.count));
    const minEntry = counts.reduce((a, b) => a.count <= b.count ? a : b);
    if (
      (playersByTable.value[player.table] || []).length === maxCount &&
      maxCount - minEntry.count > 1 &&
      minEntry.table !== player.table
    ) {
      moveToTable.value = minEntry.table;
    } else {
      moveToTable.value = player.table;
    }
  } else {
    moveToTable.value = player.table;
  }

  movePlayerDialog.value = true;
}

watch(moveToTable, () => { moveToSeat.value = null; });

function confirmMovePlayer() {
  const sameTable = playerToMove.value?.table === moveToTable.value;
  const sameSeat = playerToMove.value?.seat === moveToSeat.value;
  if (!playerToMove.value || !moveToTable.value || (sameTable && (sameSeat || !moveToSeat.value))) {
    movePlayerDialog.value = false;
    return;
  }

  // Temporarily store the move details
  pendingMove = {
    playerId: playerToMove.value.id,
    toTable: moveToTable.value,
    fromTable: playerToMove.value.table,
    toSeat: moveToSeat.value || null  // null means assign a random available seat
  };

  // Same-table seat change can't affect balance — execute immediately
  if (sameTable) {
    executeMove();
    return;
  }

  // Simulate the move to check for balance
  const futureTableCounts = { ...playersByTable.value };
  const player = players.value.find(p => p.id === pendingMove.playerId);

  // Simulate removing from old table
  if (pendingMove.fromTable && futureTableCounts[pendingMove.fromTable]) {
    futureTableCounts[pendingMove.fromTable] = futureTableCounts[pendingMove.fromTable].filter(p => p.id !== pendingMove.playerId);
  }

  // Simulate adding to new table
  if (!futureTableCounts[pendingMove.toTable]) {
    futureTableCounts[pendingMove.toTable] = [];
  }
  futureTableCounts[pendingMove.toTable] = [...futureTableCounts[pendingMove.toTable], player];

  const activeTablePlayerCounts = Object.values(futureTableCounts)
    .filter(playersAtTable => playersAtTable.length > 0)
    .map(playersAtTable => playersAtTable.length);

  if (activeTablePlayerCounts.length > 1) {
    const maxCount = Math.max(...activeTablePlayerCounts);
    const minCount = Math.min(...activeTablePlayerCounts);
    if (maxCount - minCount > 1) {
      unbalancedMoveDialog.value = true;
      return; // Wait for user confirmation
    }
  }

  // If the move is balanced, execute it immediately
  executeMove();
}

function executeMove() {
  if (!pendingMove) return;
  const player = players.value.find(p => p.id === pendingMove.playerId);
  if (player) {
    player.table = pendingMove.toTable;
    player.seat = pendingMove.toSeat ?? findAvailableSeat(pendingMove.toTable);
    autoSave();
  }
  unbalancedMoveDialog.value = false;
  movePlayerDialog.value = false;
  pendingMove = null;
}

// Returns the lowest seat number not already occupied at the given table.
function findAvailableSeat(tableNum, alreadyAssigned = new Set()) {
  const occupied = new Set([
    ...(playersByTable.value[tableNum] || []).map(p => p.seat),
    ...alreadyAssigned
  ]);
  for (let seat = 1; seat <= maxPlayersPerTable.value; seat++) {
    if (!occupied.has(seat)) return seat;
  }
  return occupied.size + 1; // beyond max (table is over capacity)
}

function checkTableBalance(bustedTable = null) {
  if (maxTables.value <= 1) return;
  if (bustedTable !== null) {
    // Bust-specific check: only warn if another table has 2+ more players than the busted table
    if (isTableUnbalancedAfterBust(playersByTable.value, bustedTable)) {
      rebalanceDialog.value = true;
    }
  } else {
    // General check for non-bust operations (delete, move, reseat)
    // Exclude empty tables — they are not active and should not skew the balance check
    const tableCounts = Object.values(playersByTable.value).map(p => p.length).filter(c => c > 0);
    if (tableCounts.length < 2) return;
    const maxCount = Math.max(...tableCounts);
    const minCount = Math.min(...tableCounts);
    if (maxCount - minCount > 1) {
      rebalanceDialog.value = true;
    }
  }
}

function cancelUnbalancedMove() {
  unbalancedMoveDialog.value = false;
  pendingMove = null;
  // The move dialog remains open for the user to choose another table or cancel.
}

function checkTableConsolidation() {
  if (maxTables.value <= 1) return;

  const activePlayersCount = players.value.filter(p => !isPlayerBusted(p.id)).length;
  const tablesInUse = activeTableNumbers.value.length;
  const minTablesNeeded = Math.ceil(activePlayersCount / maxPlayersPerTable.value);

  if (tablesInUse > minTablesNeeded) {
    closeTableDialog.value = true;
  }
}

function openCloseTableDialog(tableNumber) {
  tableToClose.value = tableNumber;
  closeTableConfirmDialog.value = true;
}

function handleReseatChoice(shouldReseat) {
  if (!tableToClose.value) {
    closeTableConfirmDialog.value = false;
    return;
  }

  const playersAtTable = players.value.filter(
    p => p.table === tableToClose.value && !isPlayerBusted(p.id)
  );

  if (shouldReseat) {
    // Re-seat players at other tables, distributing as evenly as possible
    const otherTables = activeTableNumbers.value.filter(t => t !== tableToClose.value);
    if (otherTables.length > 0) {
      // Track seats assigned in this batch so concurrent moves don't collide
      const batchAssigned = {}; // tableNum → Set of seats assigned so far
      // Count current occupancy of each other table
      const occupancy = {};
      otherTables.forEach(t => {
        occupancy[t] = players.value.filter(p => p.table === t && !isPlayerBusted(p.id) && p.table !== tableToClose.value).length;
        batchAssigned[t] = new Set();
      });
      playersAtTable.forEach(player => {
        // Always pick the table with the fewest players so far
        const targetTable = otherTables.reduce((a, b) => occupancy[a] <= occupancy[b] ? a : b);
        player.table = targetTable;
        player.seat = findAvailableSeat(targetTable, batchAssigned[targetTable]);
        batchAssigned[targetTable].add(player.seat);
        occupancy[targetTable]++;
      });
    } else {
      // No other tables, just unseat them
      playersAtTable.forEach(player => {
        player.table = null;
        player.seat = null;
      });
    }
  } else {
    // Just unseat them
    playersAtTable.forEach(player => {
      player.table = null;
      player.seat = null;
    });
  }

  closeTableConfirmDialog.value = false;
  tableToClose.value = null;
  autoSave();
  checkTableBalance();
}
</script>

<style scoped>
.table-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.table-card,
.unseated-card {
  width: 100%;
}

@media (max-width: 599px) {
  .table-grid {
    grid-template-columns: 1fr;
  }
}
</style>